import React from 'react';
import styled from '@emotion/styled';
import { EmergencyMode, ControlMode, Blinker, Gear, DriveMode } from 'core/models/drive';
import { ControlModeButton } from '../../molecules/ControlModeButton';
import { EmergencyModeButton } from '../../molecules/EmergencyModeButton';
import { BlinkerBox } from 'components/views/molecules/BlinkerBox';
import { GearSelector } from '../../molecules/GearSelector';

const Frame = styled.div({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'center',
    ['> *']: {
        display: 'block',
        height: '18%',
        width: '100%',
    },
    height: '100%',
    minHeight: '150px',
    boxSizing: 'border-box',
});

const Row = styled.div({
    margin: '5px 0',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'start',
});

const EmergencyLabel = styled.span<{ emergency: boolean }>(props => ({
    color: props.emergency ? 'red' : 'darkgray',
    border: props.emergency ? 'solid 3px red' : 'solid 3px darkgray',
    boxShadow: props.emergency ? '0 0 4px red' : 'none',
    fontWeight: 'bold',
    textAlign: 'center',
    padding: '0.5ex',
    borderRadius: '5px',
    minWidth: '8em',
    fontSize: 'x-small',
    ':after': {
        content: '"EMERGENCY"'
    }
}));

function driveModeLabel(driveMode: DriveMode) {
    switch (driveMode) {
        case DriveMode.Auto: return ['REMOTE' /* yes, this is correct */, 'darkgray', 'solid 3px darkgray', 'none'];
        case DriveMode.Remote: return ['REMOTE', '#5cb85c', 'solid 3px #5cb85c', '0 0 4px #5cb85c'];
        case DriveMode.Manual: return ['Manual', 'red', 'solid 3px red', '0 0 4px red'];
    }
}

const DriveModeLabel = styled.span<{ driveMode: DriveMode }>(props => {
    const [label, color, border, boxShadow] = driveModeLabel(props.driveMode);
    return {
        color,
        border,
        boxShadow,
        fontWeight: 'bolder',
        textAlign: 'center',
        padding: '0.5ex',
        borderRadius: '5px',
        minWidth: '8em',
        fontSize: 'x-small',
        ':after': {
            content: `"${label}"`
        }
    };
});

export type DriveSwitchProps = {
    className?: string;
    emergencyMode: EmergencyMode;
    controlMode: ControlMode; // desired control mode
    blinker: Blinker | null;
    gear: Gear;
    onEmergencyModeToggle: () => void;
    onControlModeToggle: () => void;
    onBlinkerChange: (blinker: Blinker | null) => void;
    onSelectGear: (gear: Gear) => void;
    actual?: {
        emergencyMode?: EmergencyMode;
        driveMode?: DriveMode;
        blinker?: Blinker | null;
        gear?: Gear;
    };
    isControlAllowed: boolean;
}

export const DriveSwitch = (props: DriveSwitchProps) => {
    const { actual, isControlAllowed } = props;
    const {
        emergencyMode: actualEmergencyMode = false,
        driveMode: actualDriveMode = DriveMode.Auto,
    } = actual ?? {};

    return (
        <Frame className={props.className}>
            <Row>
                <EmergencyLabel emergency={actualEmergencyMode != EmergencyMode.Normal} />
                <EmergencyModeButton mode={props.emergencyMode} onClick={props.onEmergencyModeToggle} />
            </Row>
            <Row>
                <DriveModeLabel driveMode={actualDriveMode} />
                <ControlModeButton mode={props.controlMode} onClick={props.onControlModeToggle} disabled={!isControlAllowed || actualDriveMode == DriveMode.Manual} />
            </Row>
            <Row>
                {
                    isControlAllowed
                        ? <BlinkerBox blinker={props.blinker} onSelect={props.onBlinkerChange} />
                        : <></>
                }
            </Row>
            <Row>
                {
                    isControlAllowed
                        ? <GearSelector gear={props.gear} onSelect={props.onSelectGear} />
                        : <></>
                }
            </Row>
        </Frame>
    );
};
